import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  LayoutGrid,
  ClipboardList,
  Trash2,
  Share2,
  Check,
  Loader2,
  X,
  ChevronDown,
} from "lucide-react";
import {
  getScorecardTemplates,
  createScorecardTemplate,
  getMyScorecards,
  shareScorecard,
} from "@/lib/api";

// ─── constants ────────────────────────────────────────────────────────────────

const DEFAULT_TEMPLATES = [
  {
    id: "default_technical",
    name: "Technical",
    isDefault: true,
    competencies: ["Problem Solving", "Coding Skills", "System Design", "Communication", "Culture Fit"],
  },
  {
    id: "default_behavioral",
    name: "Behavioral",
    isDefault: true,
    competencies: ["Leadership", "Teamwork", "Conflict Resolution", "Adaptability", "Initiative"],
  },
  {
    id: "default_product",
    name: "Product",
    isDefault: true,
    competencies: ["Product Sense", "Data-Driven Thinking", "Prioritisation", "Stakeholder Mgmt", "Vision"],
  },
  {
    id: "default_leadership",
    name: "Leadership",
    isDefault: true,
    competencies: ["Strategic Thinking", "Team Building", "Execution", "Communication", "Decision Making"],
  },
];

const RECOMMENDATION_COLORS = {
  strong_hire: "text-green-700 bg-green-100",
  hire: "text-green-600 bg-green-50",
  no_hire: "text-red-600 bg-red-50",
  strong_no_hire: "text-red-700 bg-red-100",
};
const RECOMMENDATION_LABELS = {
  strong_hire: "Strong Hire",
  hire: "Hire",
  no_hire: "No Hire",
  strong_no_hire: "Strong No Hire",
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.08 } }),
};

// ─── CreateTemplateModal ──────────────────────────────────────────────────────

function CreateTemplateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [competencies, setCompetencies] = useState([""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addComp = () => setCompetencies((c) => [...c, ""]);
  const removeComp = (i) => setCompetencies((c) => c.filter((_, idx) => idx !== i));
  const updateComp = (i, val) =>
    setCompetencies((c) => c.map((v, idx) => (idx === i ? val : v)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = competencies.map((c) => c.trim()).filter(Boolean);
    if (!name.trim()) { setError("Template name is required."); return; }
    if (cleaned.length === 0) { setError("Add at least one competency."); return; }
    setError("");
    setSaving(true);
    try {
      const { data } = await createScorecardTemplate({ name: name.trim(), competencies: cleaned });
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail ?? "Failed to create template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200">
          <h2 className="font-display text-2xl">Create custom template</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-stone-400 hover:bg-stone-100 hover:text-stone-900 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-stone-500 mb-1.5">
              Template name
            </label>
            <input
              type="text"
              placeholder="e.g. Sales, Design, Executive…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-stone-900 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-stone-500 mb-2">
              Competencies
            </label>
            <div className="space-y-2">
              {competencies.map((comp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={`Competency ${i + 1}`}
                    value={comp}
                    onChange={(e) => updateComp(i, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-stone-900 transition-colors"
                  />
                  {competencies.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeComp(i)}
                      className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addComp}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 transition-colors"
            >
              <Plus size={13} /> Add competency
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60 inline-flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Create template
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ─── TemplateCard ─────────────────────────────────────────────────────────────

function TemplateCard({ template, onUse, index }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      custom={index}
      className="glass rounded-2xl p-5 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-stone-900">{template.name}</h3>
          {template.isDefault && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Default</span>
          )}
        </div>
        <span className="text-xs font-mono text-stone-400 shrink-0">
          {template.competencies?.length ?? 0} criteria
        </span>
      </div>
      <ul className="space-y-1 flex-1">
        {(template.competencies ?? []).map((c) => {
          const label = typeof c === "string" ? c : c.name ?? c.label ?? String(c);
          return (
            <li key={label} className="flex items-center gap-2 text-sm text-stone-600">
              <span className="w-1 h-1 rounded-full bg-stone-400 shrink-0" />
              {label}
            </li>
          );
        })}
      </ul>
      <button
        onClick={() => onUse?.(template)}
        className="btn-primary text-sm w-full justify-center"
      >
        Use template
      </button>
    </motion.div>
  );
}

// ─── RecentScorecardsTable ────────────────────────────────────────────────────

function RecentScorecardsTable() {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharingId, setSharingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMyScorecards();
        setScorecards(data ?? []);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);

  const handleShare = async (sc) => {
    const id = sc.id ?? sc._id;
    setSharingId(id);
    try {
      await shareScorecard(id);
      setScorecards((prev) =>
        prev.map((s) => ((s.id ?? s._id) === id ? { ...s, shared: true } : s))
      );
    } catch (_) {}
    finally { setSharingId(null); }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-14 rounded-xl bg-stone-200 animate-pulse" />
        ))}
      </div>
    );
  }

  if (scorecards.length === 0) {
    return (
      <div className="text-center py-16 text-stone-500">
        <ClipboardList size={36} className="mx-auto mb-3 opacity-30" />
        <p className="font-medium">No scorecards yet</p>
        <p className="text-sm mt-1">Scorecards submitted after interviews will appear here.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-100">
            {["Candidate", "Job", "Score", "Recommendation", "Shared", "Date"].map((h) => (
              <th key={h} className="text-left px-4 py-3 text-xs font-mono uppercase tracking-widest text-stone-400 font-normal">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scorecards.map((sc, i) => {
            const id = sc.id ?? sc._id;
            const rec = sc.recommendation ?? "";
            const recLabel = RECOMMENDATION_LABELS[rec] ?? rec;
            const recColor = RECOMMENDATION_COLORS[rec] ?? "text-stone-600 bg-stone-100";
            const dateStr = sc.created_at
              ? new Date(sc.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
              : "—";
            return (
              <motion.tr
                key={id ?? i}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={i * 0.06}
                className="border-b border-stone-50 hover:bg-stone-50 transition-colors"
              >
                <td className="px-4 py-3 font-medium text-stone-900">{sc.candidate_name ?? "—"}</td>
                <td className="px-4 py-3 text-stone-600">{sc.job_title ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-stone-700">
                  {sc.score != null ? `${sc.score}/5` : "—"}
                </td>
                <td className="px-4 py-3">
                  {rec ? (
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${recColor}`}>
                      {recLabel}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => !sc.shared && handleShare(sc)}
                    disabled={sc.shared || sharingId === id}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      sc.shared
                        ? "bg-green-100 text-green-700 cursor-default"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {sharingId === id ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : sc.shared ? (
                      <Check size={11} />
                    ) : (
                      <Share2 size={11} />
                    )}
                    {sc.shared ? "Shared" : "Share"}
                  </button>
                </td>
                <td className="px-4 py-3 text-stone-400 text-xs">{dateStr}</td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function Scorecards() {
  const [activeTab, setActiveTab] = useState("templates");
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getScorecardTemplates();
        // Backend returns { defaults: [...], custom: [...] } — use custom only
        // (defaults are rendered from the local DEFAULT_TEMPLATES constant).
        const custom = Array.isArray(data) ? data : (data?.custom ?? []);
        setCustomTemplates(Array.isArray(custom) ? custom : []);
      } catch (_) {}
      finally { setLoadingCustom(false); }
    })();
  }, []);

  const allTemplates = [
    ...DEFAULT_TEMPLATES,
    ...customTemplates.filter((t) => !t.isDefault),
  ];

  const handleTemplateCreated = (newTemplate) => {
    setCustomTemplates((prev) => [...prev, newTemplate]);
  };

  const handleUseTemplate = (template) => {
    // In a real flow this would open ScorecardForm with the template pre-selected.
    // Here we just surface the template name for now.
    toast.error(`Template "${template.name}" selected. Open ScorecardForm from an interview page to use it.`);
  };

  return (
    <div className="container-x py-10 md:py-14">
      {/* Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0}
        className="flex items-start justify-between gap-4 mb-8 flex-wrap"
      >
        <div>
          <span className="overline">Company</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">Scorecards</h1>
          <p className="mt-2 text-stone-500 max-w-lg">
            Standardise your hiring decisions with structured evaluation templates.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary inline-flex items-center gap-2 shrink-0"
        >
          <Plus size={16} /> Create template
        </button>
      </motion.div>

      {/* Tabs */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={1}
        className="inline-flex p-1 mb-8 rounded-full border border-stone-300 bg-white"
      >
        {[
          ["templates", <LayoutGrid size={14} />, "Templates"],
          ["recent", <ClipboardList size={14} />, "Recent scorecards"],
        ].map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
              activeTab === key
                ? "bg-stone-900 text-stone-50"
                : "text-stone-600 hover:text-stone-900"
            }`}
          >
            {icon} {label}
          </button>
        ))}
      </motion.div>

      {/* Templates tab */}
      {activeTab === "templates" && (
        <div>
          {loadingCustom ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-56 rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {allTemplates.map((t, i) => (
                  <TemplateCard
                    key={t.id ?? t.template_id ?? t._id ?? i}
                    template={t}
                    index={i}
                    onUse={handleUseTemplate}
                  />
                ))}

                {/* Create custom CTA card */}
                <motion.button
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  custom={allTemplates.length}
                  onClick={() => setShowCreateModal(true)}
                  className="glass rounded-2xl p-5 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-stone-300 hover:border-stone-500 hover:bg-stone-50 transition-colors group min-h-[200px]"
                >
                  <div className="w-10 h-10 rounded-full bg-stone-100 group-hover:bg-stone-900 flex items-center justify-center transition-colors">
                    <Plus size={18} className="text-stone-500 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-stone-700 text-sm">Create custom</p>
                    <p className="text-xs text-stone-400 mt-0.5">Build your own template</p>
                  </div>
                </motion.button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recent scorecards tab */}
      {activeTab === "recent" && (
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <RecentScorecardsTable />
        </motion.div>
      )}

      {/* Create template modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateTemplateModal
            onClose={() => setShowCreateModal(false)}
            onCreated={handleTemplateCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
